// Eleplan for Chrome (chrome-extension/ in this repo): the app finds it, hands it
// a token and disconnects it with chrome.runtime.sendMessage. Chrome only exposes
// that on a page an installed extension lists in externally_connectable, and the
// app only talks to the extension IDs below, so another extension never gets a token.
import { Platform } from 'react-native';
import config from '../config';
import { errandAction, type ErrandIds } from './errands';
import { getElectronBridge, isChromePanel } from './platform';

// The Web Store item. A local build of chrome-extension/ (its manifest key pins its
// ID, and that key is public) is trusted only outside production, so copying the
// key can't get a production token.
const STORE_ID = 'ojbejnchgiphlhcgnoknhpplmadhaffc';
const LOCAL_BUILD_ID = 'iimgcofacmbbgeoedlbinnoldlenifaj';
// Unknown means production: the stricter list.
export const CHROME_EXTENSION_IDS = (config?.env ?? 'production') === 'production' ? [STORE_ID] : [STORE_ID, LOCAL_BUILD_ID];
export const CHROME_EXTENSION_PAGE = 'https://eleplan.com/chrome';

const REPLY_MS = 1500;
// Connecting and disconnecting call the server from the extension (up to 25 s there).
const SERVER_REPLY_MS = 30_000;

// awaiting: Connect was just pressed in the extension (versions before 0.1.6 don't say).
// account: whose Eleplan account it's connected for (0.1.8 on; older ones don't say).
// pairs: without a press of Connect, it holds a token (the desktop app's pairing) and asks for a yes in its own popup.
// blocked: Chrome isn't letting it reach Eleplan's server (0.1.16 on), so a token handed over can't be used.
type Reply = { installed?: boolean; connected?: boolean; awaiting?: boolean; account?: string; pairs?: boolean; blocked?: boolean; reason?: string };
type Runtime = { sendMessage?: (id: string, message: unknown, reply: (response?: Reply) => void) => void; lastError?: unknown };

export interface ChromeExtension { id: string; connected: boolean; awaiting?: boolean; account?: string; pairs?: boolean; blocked?: boolean }
export type ConnectResult =
  | { status: 'connected' } | { status: 'not_installed' } | { status: 'upgrade' } | { status: 'press_connect' } | { status: 'other_account' }
  // Chrome keeps it from the server; nothing was minted, so a pairing code is still good.
  | { status: 'blocked' }
  | { status: 'failed'; message: string };

/** Connected, but for another Eleplan account (a shared computer). Unknown on extensions too old to say. */
export function connectedForSomeoneElse(extension: ChromeExtension | null | undefined, cognitoId: string | undefined): boolean {
  return !!extension?.connected && typeof extension.account === 'string' && !!cognitoId && extension.account !== cognitoId;
}

function runtime(): Runtime | undefined {
  if (Platform.OS !== 'web') return undefined;
  return (globalThis as { chrome?: { runtime?: Runtime } }).chrome?.runtime;
}

/** One message to one extension; null when it isn't installed or doesn't answer. */
function ask(id: string, message: Record<string, unknown>, ms = REPLY_MS): Promise<Reply | null> {
  const rt = runtime();
  if (!rt?.sendMessage) return Promise.resolve(null);
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    try {
      rt.sendMessage!(id, message, (reply) => {
        clearTimeout(timer);
        // Reading lastError is how Chrome is told a missing extension was expected.
        resolve(rt.lastError || !reply ? null : reply);
      });
    } catch {
      clearTimeout(timer);
      resolve(null);
    }
  });
}

export async function findChromeExtension(): Promise<ChromeExtension | null> {
  for (const id of CHROME_EXTENSION_IDS) {
    const reply = await ask(id, { type: 'eleplan/status' });
    if (reply?.installed) {
      return {
        id, connected: !!reply.connected,
        ...(typeof reply.awaiting === 'boolean' ? { awaiting: reply.awaiting } : {}),
        ...(typeof reply.account === 'string' ? { account: reply.account } : {}),
        ...(reply.pairs === true ? { pairs: true } : {}),
        ...(reply.blocked === true ? { blocked: true } : {}),
      };
    }
  }
  return null;
}

// The extension's popup shows Allow, which asks Chrome for the server back.
export const SERVER_BLOCKED_MESSAGE = 'Chrome is keeping Eleplan for Chrome from reaching Eleplan. Click the Eleplan icon in Chrome’s toolbar, choose Allow, then try again.';

/**
 * Connects this browser for the signed-in person. A connected one is left as it is.
 * pairingCode: the desktop app opened this browser to connect it (errands API
 * pair_start). An extension that pairs asks the person once in its own popup instead
 * of needing Connect pressed first.
 */
export async function connectChromeExtension(ids: ErrandIds, pairingCode?: string): Promise<ConnectResult> {
  const extension = await findChromeExtension();
  if (!extension) return { status: 'not_installed' };
  if (connectedForSomeoneElse(extension, ids.cognitoId)) return { status: 'other_account' };
  // Connected or not, it can't do anything until Chrome lets it reach the server.
  if (extension.blocked) return { status: 'blocked' };
  if (extension.connected) return { status: 'connected' };
  const paired = !!pairingCode && extension.pairs === true;
  // Otherwise it only takes a token right after Connect is pressed in it; minting one
  // then would leave a connection on the server with no browser behind it.
  if (!paired && extension.awaiting === false) return { status: 'press_connect' };
  const res = paired ? await errandAction(ids, 'pair_finish', { code: pairingCode }) : await errandAction(ids, 'browser_token');
  // Eleplan for Chrome comes with paid plans: the plan's subscription doesn't include it.
  if ((res as { status?: number }).status === 402) return { status: 'upgrade' };
  const token = res.payload?.token;
  if (!res.success || typeof token !== 'string') return { status: 'failed', message: res.message || 'I couldn’t connect it. Try again.' };
  const reply = await ask(extension.id, { type: 'eleplan/connect', token, account: ids.cognitoId }, SERVER_REPLY_MS);
  if (reply?.connected) return { status: 'connected' };
  return { status: 'failed', message: reply?.reason === 'server_blocked' ? SERVER_BLOCKED_MESSAGE : 'Chrome didn’t answer. Try again.' };
}

/** The desktop app only: the browser on this computer with Eleplan for Chrome, by name. */
export async function findLocalBrowser(): Promise<string | null> {
  const local = await getElectronBridge()?.browser?.local().catch(() => null);
  return local?.name || null;
}

export type PairResult = { status: 'opened'; name?: string } | { status: 'upgrade' } | { status: 'failed'; message: string };

/**
 * The desktop app only: opens the browser here to connect it (errands API
 * pair_start). That browser's page finishes it, signed in as this same person.
 */
export async function pairLocalBrowser(ids: ErrandIds): Promise<PairResult> {
  const browser = getElectronBridge()?.browser;
  if (!browser) return { status: 'failed', message: 'Update the Eleplan app to connect Chrome from here.' };
  const res = await errandAction(ids, 'pair_start');
  if ((res as { status?: number }).status === 402) return { status: 'upgrade' };
  const code = res.payload?.code;
  if (!res.success || typeof code !== 'string') return { status: 'failed', message: res.message || 'I couldn’t connect it. Try again.' };
  const opened = await browser.pair(code).catch(() => null);
  return opened?.ok ? { status: 'opened', name: opened.name } : { status: 'failed', message: 'I couldn’t open your browser. Open it and try again.' };
}

/** Tells a connected extension to ask for work now: the fallback when a web push is slow or lost. */
export async function wakeChromeExtension(cognitoId?: string, open?: { projectId: number; accountId?: string; conversationId?: string | null }): Promise<void> {
  const extension = await findChromeExtension();
  const rt = runtime();
  // Someone else's connection on a shared computer has nothing of this person's to do.
  if (!extension?.connected || connectedForSomeoneElse(extension, cognitoId) || !rt?.sendMessage) return;
  // open: a browser check this tap approved. The extension (0.1.17 on) opens Ellie's chat in
  // its side panel on that conversation while the tap still counts. Ids only; not from the
  // panel itself, which is already open.
  const message = open && !isChromePanel
    ? { type: 'eleplan/wake', open: { project_id: open.projectId, conversation_id: open.conversationId ?? null, account_id: open.accountId ?? null } }
    : { type: 'eleplan/wake' };
  try { rt.sendMessage(extension.id, message, () => { void rt.lastError; }); } catch { /* not there */ }
}

export async function disconnectChromeExtension(): Promise<boolean> {
  const extension = await findChromeExtension();
  if (!extension) return false;
  const reply = await ask(extension.id, { type: 'eleplan/disconnect' }, SERVER_REPLY_MS);
  return reply?.connected === false;
}
